import { useCallback } from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

// @TODO:
// consider adding v2 item types here too?
export const MCQ = 'MCQ'
export const CR = 'CR'
export const FRQ = 'FRQ'
export const LIKERT = 'LIKERT'
export const MPFRQ = 'MPFRQ'
export const STORY = 'STORY'
export const CLOZE = 'CLOZE'
export const ITEM_SET = 'ITEM_SET'
export const ITEM_SET2 = 'ITEM_SET2'
export const PASSAGE_BUILDER = 'PASSAGE_BUILDER'
export const HOT_TEXT = 'HOT_TEXT'

type SubTitleKey =
  | 'author.content_type.subtitle_item_stem'
  | 'author.content_type.subtitle_options'
  | 'author.content_type.subtitle_stimulus'
  | 'author.content_type.subtitle_task'
  | 'author.content_type.subtitle_parts'
  | 'author.content_type.subtitle_item_prompts'
  | 'author.content_type.subtitle_passage'

const subTitleKeys: Record<string, Record<string, SubTitleKey>> = {
  [MCQ]: {
    question: 'author.content_type.subtitle_item_stem',
    options: 'author.content_type.subtitle_options',
    passage: 'author.content_type.subtitle_stimulus',
  },
  [CR]: {
    question: 'author.content_type.subtitle_task',
    options: 'author.content_type.subtitle_parts',
    stimulus: 'author.content_type.subtitle_stimulus',
  },
  [FRQ]: {
    question: 'author.content_type.subtitle_stimulus',
    options: 'author.content_type.subtitle_item_prompts',
    passage: 'author.content_type.subtitle_passage',
  },
  [MPFRQ]: {
    question: 'author.content_type.subtitle_stimulus',
    options: 'author.content_type.subtitle_item_prompts',
    passage: 'author.content_type.subtitle_passage',
  },
  [HOT_TEXT]: {
    question: 'author.content_type.subtitle_item_stem',
    options: 'author.content_type.subtitle_options',
    passage: 'author.content_type.subtitle_stimulus',
  },
  [LIKERT]: {
    question: 'author.content_type.subtitle_item_stem',
    options: 'author.content_type.subtitle_options',
    passage: 'author.content_type.subtitle_stimulus',
  },
}

export const getSubTitle = (t: TFunction<'legacy'>, contentType: string, slot: string): string => {
  const key = subTitleKeys[contentType]?.[slot]
  return key ? t(key) : ''
}

const CONTENT_TYPE_KEY_MAP = {
  [MCQ]: 'item_types.type_mcq_short',
  [CR]: 'item_types.type_cr_short',
  [FRQ]: 'item_types.type_frq_short',
  [LIKERT]: 'item_types.type_likert_short',
  [MPFRQ]: 'item_types.type_mpfrq_short',
  [STORY]: 'item_types.type_story_short',
  [CLOZE]: 'item_types.type_cloze_short',
  [ITEM_SET]: 'item_types.type_cloze_short',
  [ITEM_SET2]: 'item_types.type_item_set_short',
  [PASSAGE_BUILDER]: 'item_types.type_passage_builder_short',
  [HOT_TEXT]: 'item_types.type_hot_text_short',
  EBSR: 'item_types.type_ebsr_short',
} as const

export const useContentTypeLabel = () => {
  const { t } = useTranslation('author')
  return useCallback(
    (type: string = ''): string => {
      if (type.includes(',')) {
        return type
          .split(',')
          .map((part) => {
            const key = CONTENT_TYPE_KEY_MAP[part.trim() as keyof typeof CONTENT_TYPE_KEY_MAP]
            if (key) return t(key)
            console.warn(`useContentTypeLabel: unknown contentType "${part.trim()}"`)
            return part.trim().split('_').join(' ')
          })
          .join(', ')
      }
      const key = CONTENT_TYPE_KEY_MAP[type as keyof typeof CONTENT_TYPE_KEY_MAP]
      if (key) return t(key)
      console.warn(`useContentTypeLabel: unknown contentType "${type}"`)
      return type.split('_').join(' ')
    },
    [t],
  )
}

const optionsKeyMap = {
  [MCQ]: 'answers',
  [HOT_TEXT]: 'answers',
  [LIKERT]: 'options',
  [MPFRQ]: 'parts',
  [CR]: 'parts',
  [FRQ]: '',
}

// https://github.com/academicmerit/ftg/blob/dev/api/src/chalicelib/lib/content_types.py
export const getOptionsKey = (contentType: string) => {
  if (contentType in optionsKeyMap) {
    return optionsKeyMap[contentType]
  }

  console.warn('getOptionsKey: unknown content type', contentType)

  return 'answers'
}
